import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, LabelM } from '@sorare/core/src/atoms/typography';
import { LeaderboardRow } from '@sorare/core/src/components/layout/LeaderboardRow';
import { Avatar } from '@sorare/core/src/components/user/Avatar';

import { ContenderBonus } from 'components/lineup/Contender/ContenderFooter/ContenderBonus';
import { ContenderScore } from 'components/lineup/Contender/ContenderFooter/ContenderScore';
import { DumbStatus } from 'components/managerTeam/Status/DumbStatus';

import { CardsLeft } from '../CardsLeft';
import type { RankingLeaderboardRow_so5Ranking } from './__generated__/index.graphql';

const AvatarContainer = styled.div`
  position: relative;
`;
const StatusContainer = styled.div`
  position: absolute;
  display: flex;
  left: 0;
  top: 0;
  transform: translate(-3px, -3px);
`;
const Infos = styled(Vertical).attrs({ gap: 0 })`
  align-items: end;
`;
const AlignRight = styled.div`
  align-self: flex-end;
`;

type Props = {
  so5Ranking: RankingLeaderboardRow_so5Ranking;
  isMyRanking?: boolean;
  onClick?: () => void;
};
export const RankingLeaderboardRow = ({
  so5Ranking,
  isMyRanking,
  onClick,
}: Props) => {
  const { ranking, score, eligibleDivisionChange, so5Lineup } = so5Ranking;
  const { bonusPoints, bonusRating, user, name, managerTeam } = so5Lineup;

  const teamName = managerTeam?.name || name;

  return (
    <LeaderboardRow
      highlighted={isMyRanking}
      rank={ranking}
      user={user}
      avatar={
        <AvatarContainer>
          {!!eligibleDivisionChange &&
            eligibleDivisionChange !== DivisionChange.STABLE && (
              <StatusContainer>
                <DumbStatus divisionChange={eligibleDivisionChange} size={2} />
              </StatusContainer>
            )}
          <Avatar user={user} size={4} rounded />
        </AvatarContainer>
      }
      caption={<LabelM color="var(--c-nd-600)">{teamName}</LabelM>}
      onClick={onClick}
    >
      <Infos>
        <Vertical gap={0}>
          <Horizontal>
            <ContenderBonus
              bonusRating={bonusRating || null}
              bonus={bonusPoints || undefined}
            />
            <ContenderScore
              score={score}
              roundingScore={false}
              Typography={BrandS}
            />
          </Horizontal>
          <AlignRight>
            <CardsLeft so5Lineup={so5Lineup} />
          </AlignRight>
        </Vertical>
      </Infos>
    </LeaderboardRow>
  );
};

RankingLeaderboardRow.fragments = {
  so5Ranking: gql`
    fragment RankingLeaderboardRow_so5Ranking on So5Ranking {
      id
      ranking
      eligibleDivisionChange
      score
      so5Lineup {
        id
        name
        bonusPoints
        bonusRating
        managerTeam {
          id
          name
        }
        user {
          slug
          ...Avatar_publicUserInfoInterface
          ...LeaderboardRow_publicUserInfoInterface
        }
        ...CardsLeft_so5Lineup
      }
    }
    ${LeaderboardRow.fragments.publicUserInfoInterface}
    ${Avatar.fragments.publicUserInfoInterface}
    ${CardsLeft.fragments.so5Lineup}
  ` as TypedDocumentNode<RankingLeaderboardRow_so5Ranking>,
};
